import * as React from "react";

import { Alert, Snackbar } from "@mui/material";
import { ExtendedAccount } from "@/types/ExtendedAccount";
import { Account, AccountNextStep, AccountType, Person, ServiceProvider, ServiceProviderType } from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { TextInput } from "@/components/atoms/TextInput";
import { AccountNextStepInput } from "@/components/molecules";
import { FormField } from "@/components/molecules/FormField";
import { ProbeUnknown } from "@/components/molecules/ProbeUnknown";
import { useOnChange, useOnValueChange, useUpdater } from "@/utils/callbacks";

import { SelectInput } from "@/components/atoms/SelectInput";
import { AccountFieldProps } from "./AccountForm";
import { NestedErrors } from "@/utils/Types";
import { Responsible, errorsOfResponsible, responsibleFromAccount, ResponsibleForm } from "./ResponsibleFields/ResponsibleFields";

export type MobileAccount = {
  readonly phoneNumber?: string;
  readonly accountType?: string;
  readonly accountRequest?: AccountNextStep;
  readonly holderOrUser?: "holder" | "user" | "both";
  readonly responsible: Responsible;
};

export type MobileAccountErrors = NestedErrors<MobileAccount> | undefined;

export const errorsOf: (r: MobileAccount) => MobileAccountErrors = (
  record
) => {
  return cleanedErrors({

    phoneNumber: record?.phoneNumber ? undefined : "required",
    accountType: record?.accountType ? undefined : "required",
    accountRequest: record?.accountRequest ? undefined : "required",
    holderOrUser: record?.holderOrUser ? undefined : "required",
    responsible: errorsOfResponsible(record?.responsible, record?.accountRequest)
  });
};

export const recordFromAccount = (account?: Account): MobileAccount => {
  if (!account) {
    return {

      responsible: responsibleFromAccount(account)
    };
  }

  return {

    phoneNumber: account.phoneNumber,
    accountType: account.accountType,
    holderOrUser: account.holderOrUser,
    accountRequest: account.nextSteps,
    responsible: responsibleFromAccount(account)
  };
};

export type MobileAccountFieldsProps = AccountFieldProps;

export const MobileAccountFields: React.FC<MobileAccountFieldsProps> = ({
  persons,
  serviceProvider,
  account,
  setBusy,
  onAccountAdded,
  updateTemplate,

  saveAccount,
  saveRecord,
  savedRecord
}) => {
  const [record, update] = React.useState(savedRecord ||recordFromAccount(account));
  const [errors, setErrors] = React.useState({} as MobileAccountErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);

  const onAccountRequestValueChange = React.useCallback(
    (accountRequest) => {
      update((s: any) => ({
        ...s,
        accountRequest,
        responsible: {}
      }));
    },
    [update]
  );

  const updateAccountHolder = useUpdater(update, "responsible");
  const onAccountTypeValueChange = useOnValueChange(update, "accountType");
  const onHolderOrUserValueChange = useOnValueChange(update, "holderOrUser");
  const onChange = useOnChange(update);

  React.useEffect(() => {
    if (!updateTemplate) {
      return;
    }

    updateTemplate({
      onNext: !serviceProvider?.serviceProviderType
        ? undefined
        : () => {
          const errors = errorsOf(record);

          if (errors) {
            setErrors(errors);
            if (saveRecord) {
              saveRecord({ source: record });
            }
            return;
          }

          const responsible = {
            id: record.responsible?.responsibleId || undefined,
            details: record.responsible?.responsible || undefined,
            bankAccount: record.responsible?.responsibleBankAccount || undefined
          };

          const data = {
            id: account?.id,
            serviceProvider: {
              id: serviceProvider.serviceProviderId,
              companyName: serviceProvider.customProviderName,
              serviceProviderType: serviceProvider.serviceProviderType
            },

            phoneNumber: record.phoneNumber,
            accountType: record.accountType,
            holderOrUser: record.holderOrUser,
            nextStep: record.accountRequest,
            responsible
          };

          if (saveRecord) {
            saveRecord({ source: record, target: data });
            return { response: {} };
          }

          setBusy(true);

          return saveAccount({
            sector: ServiceProviderType.Mobile,
            record: data
          }).then(
            (
              response: {
                data: {
                  account: ExtendedAccount;
                  newServiceProvider?: ServiceProvider;
                  newPerson?: Person;
                };
              } & { error: Error }
            ) => {
              setBusy(false);
              if (response.error) {
                setRemoteErrors(response.error.message);
                return;
              }
              if (response.data) {
                onAccountAdded(response.data.account, {
                  newServiceProvider: response.data.newServiceProvider,
                  newPerson: response.data.newPerson
                });
                return;
              }
            },
            (err: Error) => {
              console.warn({ err });
              setBusy(false);
              setRemoteErrors("Operation failed. Please try again or contact customer support.");
            }
          );
        }
    });
  }, [updateTemplate, serviceProvider, account, onAccountAdded, record, setBusy, saveAccount]);
  return (
    <>


      <FormField halfWidthByItself label="Mobile number"> <TextInput
        name="phoneNumber"
        value={record.phoneNumber || ""}
        onChange={onChange}
        error={errors?.phoneNumber}
      /> </FormField> <FormField> <ProbeUnknown
        checked={record.phoneNumber === "0000000000"}
        label={"I don't know the Phone Number"}
        update={update}
        defaultValue={{ phoneNumber: "0000000000" }}
        emptyValue={{ phoneNumber: "" }}
      /> </FormField>

      <FormField halfWidth label="Account type"> <SelectInput
        name="accountType"
        value={record.accountType || ""}
        onValueChange={onAccountTypeValueChange}
        error={errors?.accountType}
        options={accountTypeOptions}
      /> </FormField>

      <FormField halfWidth label="The person who died was"> <SelectInput
        name="holderOrUser"
        value={record.holderOrUser || ""}
        onValueChange={onHolderOrUserValueChange}
        error={errors?.holderOrUser}
        options={holderOrUserOptions}
      /> </FormField>

      <FormField label="Next steps"> <AccountNextStepInput
        onValueChange={onAccountRequestValueChange}
        value={record.accountRequest || ""}
        serviceProviderType={serviceProvider.serviceProviderType || ServiceProviderType.Other}
        error={errors?.accountRequest}
      /> </FormField>

      {record.responsible && record.accountRequest && (
        <ResponsibleForm
          serviceProvider={serviceProvider}
          persons={persons}
          nextStep={record.accountRequest}
          record={record.responsible}
          errors={errors?.responsible}
          update={updateAccountHolder}
        />
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};

const accountTypeOptions = [
  { value: AccountType.PayAsYouGo, label: "Pay as you go" },
  { value: AccountType.Contract, label: "Contract" },
  { value: AccountType.Unknown, label: "Don't know" }
];

const holderOrUserOptions = [
  { value: "holder", label: "The account holder" },
  { value: "user", label: "The account user" },
  { value: "both", label: "Both" }
];
